import { FileText, Paperclip, X } from "lucide-react";
import { useGetList, useTranslate, type Identifier } from "ra-core";
import { useRef, type ChangeEvent } from "react";
import { Button } from "@/components/ui/button";

import type { CompanyQuote, RAFile } from "../types";

/** Total size of the files of one email. */
export const MAX_ATTACHMENTS_BYTES = 20 * 1024 * 1024;

/**
 * Attachments of the compose window: files from the computer, and the PDF
 * quotes of the company (« Devis » tab) in one click.
 */
export const ComposeAttachments = ({
  value,
  onChange,
  onTooLarge,
  companyId,
}: {
  value: RAFile[];
  onChange: (files: RAFile[]) => void;
  onTooLarge: () => void;
  companyId?: Identifier;
}) => {
  const translate = useTranslate();
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: quotes = [] } = useGetList<CompanyQuote>(
    "company_quotes",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "date", order: "DESC" },
      filter: { company_id: companyId },
    },
    { enabled: companyId != null },
  );
  const quoteFiles = quotes
    .map((quote) => quote.attachments?.[0])
    .filter((file): file is RAFile => !!file?.path)
    .filter((file) => !value.some((item) => item.path === file.path));

  const size = (files: RAFile[]) =>
    files.reduce((total, file) => total + (file.rawFile?.size ?? 0), 0);

  const handleFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []).map(
      (file): RAFile => ({
        rawFile: file,
        src: URL.createObjectURL(file),
        title: file.name,
        type: file.type,
      }),
    );
    event.target.value = "";
    const next = [...value, ...files];
    if (size(next) > MAX_ATTACHMENTS_BYTES) {
      onTooLarge();
      return;
    }
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          aria-label={translate("crm.mail.compose.add_file")}
          onChange={handleFiles}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
        >
          <Paperclip className="size-4" />
          {translate("crm.mail.compose.add_file")}
        </Button>
        {quoteFiles.map((file) => (
          <Button
            key={file.path}
            type="button"
            variant="outline"
            size="sm"
            className="font-normal"
            onClick={() => onChange([...value, file])}
          >
            <FileText className="size-4 text-red-600" />
            {translate("crm.mail.compose.attach_quote", { name: file.title })}
          </Button>
        ))}
      </div>
      {value.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {value.map((file, index) => (
            <li
              key={`${file.path ?? file.src}-${index}`}
              className="flex items-center gap-1 rounded-md border px-2 py-1 text-sm"
            >
              <Paperclip className="size-3.5 text-muted-foreground" />
              <span className="max-w-56 truncate">{file.title}</span>
              <button
                type="button"
                className="cursor-pointer opacity-60 hover:opacity-100"
                aria-label={translate("crm.mail.compose.remove_file", {
                  name: file.title,
                })}
                onClick={() => onChange(value.filter((_, i) => i !== index))}
              >
                <X className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};
